import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  type OnFarmImplantQueueRecord,
  OnFarmImplantRequestError,
  useAssignOnFarmImplantQueueBarcodeIds,
} from '@/services/on-farm-implant';

import { useSourceTypeLabel } from './columnDefs';

const MAX_BARCODE_ID = 2147483647;

type AssignBarcodeIdsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  rows: OnFarmImplantQueueRecord[];
  onApplied: (
    rows: OnFarmImplantQueueRecord[],
    startingBarcodeId: number,
  ) => void;
};

const parseStartingBarcodeId = (value: string, count: number) => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const start = Number(trimmed);
  if (!Number.isSafeInteger(start) || start < 1) return null;
  if (start + Math.max(count - 1, 0) > MAX_BARCODE_ID) return null;
  return start;
};

export const AssignBarcodeIdsDialog = ({
  open,
  onOpenChange,
  sessionId,
  rows,
  onApplied,
}: AssignBarcodeIdsDialogProps) => {
  const { t } = useTranslation();
  const sourceTypeLabel = useSourceTypeLabel();

  const [startText, setStartText] = useState('');
  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: assignBarcodeIds, isPending } =
    useAssignOnFarmImplantQueueBarcodeIds();

  const start = useMemo(
    () => parseStartingBarcodeId(startText, rows.length),
    [rows.length, startText],
  );

  const showInvalid = startText.trim() !== '' && start === null;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setProblems([]);
        setStartText('');
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (rows.length === 0 || start === null) return;

    setProblems([]);

    try {
      await assignBarcodeIds({
        sessionId,
        input: {
          sessionQueueIds: rows.map((row) => row.sessionQueueId),
          startingBarcodeId: start,
          versions: collectVersions(rows),
        },
      });

      onApplied(rows, start);
      handleOpenChange(false);

      toast.add({
        data: { variant: 'success' },
        title:
          rows.length === 1
            ? t('onFarmImplant.implantQueue.assignBarcodeIds.successSingle', {
                first: start,
              })
            : t('onFarmImplant.implantQueue.assignBarcodeIds.success', {
                count: rows.length,
                first: start,
                last: start + rows.length - 1,
              }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to assign implant queue Barcode IDs:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.implantQueue.assignBarcodeIds.failed'),
      });
    }
  }, [
    assignBarcodeIds,
    handleOpenChange,
    onApplied,
    rows,
    sessionId,
    start,
    t,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.implantQueue.assignBarcodeIds.title')}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.implantQueue.assignBarcodeIds.description', {
              count: rows.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <label
            htmlFor="assign-barcode-ids-start"
            className="flex flex-col gap-1 text-xs font-medium text-gray-700"
          >
            {t('onFarmImplant.implantQueue.assignBarcodeIds.startLabel')}
            <Input
              id="assign-barcode-ids-start"
              inputMode="numeric"
              autoFocus
              className="h-8 w-48 text-sm"
              value={startText}
              aria-invalid={showInvalid}
              onChange={(event) => setStartText(event.target.value)}
            />
          </label>
          {showInvalid && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.implantQueue.assignBarcodeIds.invalidStart')}
            </p>
          )}

          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-gray-500">
                  <th className="py-1 font-medium">
                    {t('onFarmImplant.implantQueue.queueOrder')}
                  </th>
                  <th className="py-1 font-medium">
                    {t('onFarmImplant.implantQueue.embryo')}
                  </th>
                  <th className="py-1 font-medium">
                    {t('onFarmImplant.implantQueue.sourceType')}
                  </th>
                  <th className="py-1 font-medium">
                    {t(
                      'onFarmImplant.implantQueue.assignBarcodeIds.currentBarcodeId',
                    )}
                  </th>
                  <th className="py-1 font-medium">
                    {t(
                      'onFarmImplant.implantQueue.assignBarcodeIds.newBarcodeId',
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.sessionQueueId} className="border-t">
                    <td className="py-1.5">{row.queueOrder}</td>
                    <td className="py-1.5">{row.embryoName ?? '—'}</td>
                    <td className="py-1.5">
                      {sourceTypeLabel(row.sourceType)}
                    </td>
                    <td className="py-1.5">{row.dairyCompId ?? '—'}</td>
                    <td className="py-1.5 font-medium">
                      {start === null ? '—' : start + index}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={rows.length === 0 || start === null || isPending}
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.implantQueue.assignBarcodeIds.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
